import React, { useState } from "react";
import { UserSearch, ExternalLink, ChevronDown } from "lucide-react";
import { SkillsPicker } from "@/components/SkillsUI";
import { PROFS, LEVELS, CS_TR, buildCandidatesUrl } from "@/lib/candidateSearch";

const Pills = ({ items, value, onPick, labels, prefix }) => (
  <div className="flex flex-wrap gap-2">
    {items.map((id) => (
      <button key={id} type="button" onClick={() => onPick(value === id ? "" : id)}
        data-testid={`${prefix}-${id}`}
        className={`px-3 py-1.5 text-xs font-mono border transition-colors ${value === id ? "border-gold-500 bg-gold-900/25 text-gold-300" : "border-zinc-700 text-zinc-400 hover:border-zinc-500"}`}>
        {labels[id]}
      </button>
    ))}
  </div>
);

export function CandidateSearchPanel({ lang = "sk" }) {
  const t = CS_TR[lang] || CS_TR.sk;
  const [skills, setSkills] = useState([]);
  const [prof, setProf] = useState("");
  const [level, setLevel] = useState("");
  const [open, setOpen] = useState(false);
  const url = buildCandidatesUrl({ skills, prof, level });
  const label = "block text-[10px] font-mono uppercase tracking-widest text-gold-500 mb-2";

  return (
    <section className="mt-8 border border-gold-600/50 bg-gradient-to-br from-gold-950/30 to-black p-6 sm:p-8" data-testid="candidate-search-panel">
      <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.25em] text-gold-400 mb-3">
        <UserSearch className="w-3 h-3" /> {t.eyebrow}
      </div>
      <h2 className="font-display font-black text-2xl sm:text-3xl text-white leading-tight mb-5">{t.title}</h2>
      <div className="space-y-5">
        <div>
          <div className={label}>{t.prof}</div>
          <Pills items={PROFS} value={prof} labels={t.profs} prefix="cand-prof"
            onPick={(v) => { setProf(v); if (!v) setLevel(""); }} />
        </div>
        {prof && (
          <div data-testid="cand-level-block">
            <div className={label}>{t.level}</div>
            <Pills items={LEVELS} value={level} labels={t.levels} prefix="cand-level" onPick={setLevel} />
          </div>
        )}
        <div>
          <button type="button" onClick={() => setOpen(!open)} data-testid="cand-skills-toggle"
            className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-gold-300 hover:text-gold-200">
            <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
            {t.skills} {skills.length > 0 && <span data-testid="cand-skills-count">({skills.length})</span>}
          </button>
          {open && <div className="mt-3"><SkillsPicker selected={skills} onChange={setSkills} lang={lang} /></div>}
        </div>
      </div>
      <a href={url} target="_blank" rel="noopener noreferrer" data-testid="cand-search-saubereko-btn"
        className="mt-6 inline-flex items-center gap-2 px-6 py-3.5 bg-gold-500 hover:bg-gold-400 text-black font-mono uppercase tracking-wider text-xs font-bold transition-colors">
        <ExternalLink className="w-4 h-4" /> {t.btn}
      </a>
      <p className="text-sm text-zinc-400 mt-4 max-w-2xl leading-relaxed" data-testid="cand-search-pricing-text">{t.info}</p>
    </section>
  );
}
